import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getSupportedLanguages} from '../i18n/config';

interface LanguageOption {
  code: string;
  label: string;
  flagClass: string;
}

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const supportedLanguageCodes = getSupportedLanguages();

  const languages: LanguageOption[] = [
    {code: 'pl', label: 'polski', flagClass: 'fi fi-pl'},
    {code: 'en', label: 'English', flagClass: 'fi fi-gb'},
    {code: 'de', label: 'Deutsch', flagClass: 'fi fi-de'},
    {code: 'fr', label: 'français', flagClass: 'fi fi-fr'},
    {code: 'it', label: 'italiano', flagClass: 'fi fi-it'}
  ].filter(lang => supportedLanguageCodes.includes(lang.code));

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-white hover:text-gray-300 font-medium px-1 py-1 rounded transition-colors flex items-center"
        aria-label="Switch language"
        aria-expanded={isOpen}
      >
        <span className={currentLanguage.flagClass}></span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-32 bg-gray-700 rounded-s border border-gray-700 z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full text-left px-2 py-1 text-s flex items-center gap-1.5 hover:bg-gray-600 transition-colors ${
                language.code === i18n.language ? 'bg-gray-600 font-semibold' : ''
              }`}
            >
              <span className={language.flagClass}></span>
              <span className="text-white pl-2">{language.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

